import React, { useLayoutEffect, useRef, useState } from "react";
import "./BracketDisplay.css";
import BracketVideo from "./BracketVideo.jsx";
import BracketHeader from "./BracketHeader.jsx";
import BracketLines from "./BracketLines.jsx";
import BracketRounds from "./BracketRounds.jsx";
import useBracketLines from "../Hooks/useBracketLines.js";
import {
 getRoundLabel,
 isTeamAlreadyPlaced,
 gameHasScore,
} from "../Utils/helpers.js";
import { generateMatchTops } from "../Utils/layoutHelpers.js";

function BracketDisplay({
 bracketData,
 games,
 setGames,
 fetchGames,
 navigate,
 setSelectedMatchup,
 setIsCreateModalOpen,
 handleDeleteGame,
 setEditGame,
 setIsEditModalOpen,
 readOnly,
 colorScheme,
 slug,
 division,
 gameDetailsTextColor = "#000",
 setBracketData,
}) {
 const [draggedTeam, setDraggedTeam] = useState(null);
 const [measuredHeights, setMeasuredHeights] = useState({});

 const matchRefs = useRef({});
 const containerRef = useRef(null);

 // Measure actual rendered match box heights
 useLayoutEffect(() => {
  if (!bracketData) return;

  const measureMatches = () => {
   const nextHeights = {};

   Object.entries(matchRefs.current).forEach(([matchId, element]) => {
    if (!element) return;

    nextHeights[matchId] =
     element.getBoundingClientRect().height;
   });

   setMeasuredHeights((previous) => {
    const previousKeys = Object.keys(previous);
    const nextKeys = Object.keys(nextHeights);

    const unchanged =
     previousKeys.length === nextKeys.length &&
     nextKeys.every(
      (key) => previous[key] === nextHeights[key]
     );

    return unchanged ? previous : nextHeights;
   });
  };

  measureMatches();

  const resizeObserver = new ResizeObserver(() => {
   measureMatches();
  });

  Object.values(matchRefs.current).forEach((element) => {
   if (element) {
    resizeObserver.observe(element);
   }
  });

  return () => {
   resizeObserver.disconnect();
  };
 }, [bracketData, games, readOnly]);

 // Generate positions from real match heights
 let matchTops = [[]];
 let visibleMatchIndexes = [];

 if (bracketData) {
  const result = generateMatchTops(
   bracketData,
   measuredHeights
  );

  matchTops = result.matchTops;
  visibleMatchIndexes = result.visibleMatchIndexes;
 }

 // Calculate required container height
 let containerHeight = 0;

 if (bracketData) {
  bracketData.rounds.forEach((round, roundIndex) => {
   round.forEach((match, matchIndex) => {
    const top = matchTops[roundIndex]?.[matchIndex];

    if (typeof top !== "number") return;

    const height =
     measuredHeights[match.matchId] || 60;

    containerHeight = Math.max(
     containerHeight,
     top + height
    );
   });
  });
 }

 containerHeight += 20;

 // Connector lines
 const { svgSize, lines } = useBracketLines(
  bracketData,
  games,
  containerRef,
  matchRefs
 );
  console.log("BRACKET DATA:", bracketData);
  console.log("ROUND 1:", bracketData?.rounds?.[0]);
 if (!bracketData) return null;

 return (
  <div className="bracket-display-main-container">
   <div>
    <BracketHeader
     bracketData={bracketData}
     getRoundLabel={getRoundLabel}
     readOnly={readOnly}
    />

    <div
     className="bracket-display-container"
     ref={containerRef}
     style={{ minHeight: `${containerHeight}px` }}
    >
     <BracketLines
      lines={lines}
      svgSize={svgSize}
      colorScheme={colorScheme}
     />

     <BracketRounds
      bracketData={bracketData}
      games={games}
      matchRefs={matchRefs}
      matchTops={matchTops}
      visibleMatchIndexes={visibleMatchIndexes}
      matchHeight={60}
      readOnly={readOnly}
      slug={slug}
      navigate={navigate}
      division={division}
      setSelectedMatchup={setSelectedMatchup}
      setIsCreateModalOpen={setIsCreateModalOpen}
      draggedTeam={draggedTeam}
      setDraggedTeam={setDraggedTeam}
      setBracketData={setBracketData}
      fetchGames={fetchGames}
      setGames={setGames}
      isTeamAlreadyPlaced={isTeamAlreadyPlaced}
      gameHasScore={gameHasScore}
      setEditGame={setEditGame}
      setIsEditModalOpen={setIsEditModalOpen}
      handleDeleteGame={handleDeleteGame}
      colorScheme={colorScheme}
      gameDetailsTextColor={gameDetailsTextColor}
     />
    </div>
   </div>

   <div className="bracket-display-video-container">
    <BracketVideo division={division} />
   </div>
  </div>
 );
}

export default BracketDisplay;